import {Box, IconButton, Typography} from '@mui/material';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {t} from 'i18next';
import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ReactComponent as InfoIcon} from '../../../src/assets/images/info-icon.svg';
import {
  IAddFolderGroupRequest,
  IclassificationList,
  IMoveTagOrTagGroupPayload,
  ITagsList,
} from '../../api/types/tagFoldering.type';
import {
  useGetDocumentForTagAndFolder,
  useGetNavigationMasterListId,
} from '../../hooks/document/useDocument';
import {
  useAddFolderGroup,
  useAddTag,
  useAddTagGroup,
  useDeleteFolderGroupOrFolder,
  useDeleteTagGroupOrTag,
  useEditTagOrTagGroup,
  useGetEditTagOrTagGroup,
  useGetFolderOrFolderGroup,
  useGetFoldersList,
  useGetTagsFoldersViewData,
  useGetTagsList,
  useMoveFolderOrFolderGroup,
  useMoveTagOrTagGroup,
  useUpdateFolderOrFolderGroup,
} from '../../hooks/tagsFoldering/useTagsFoldering';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../redux/slice/docListSlice';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../redux/slice/docViewSlice';
import {RootState} from '../../redux/store';
import {
  BgNavigationTypes,
  existingObjErrCode,
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  SourceTypeContextCode,
  TAG_NAME_REGEX,
  ToastType,
} from '../../shared/constants';
import {ExtendedTreeItemProps} from '../../shared/interfaces';
import {ISelectItem} from '../../shared/types';
import ActionMenuWrapper from '../action-menu/action-menu-wrapper/ActionMenuWrapper';
import ArrCheckbox from '../common/arr-checkbox/ArrCheckbox';
import ArrDocMaxLimitWarning from '../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import ArrLoadingSpinner from '../common/arr-loading-spinner/ArrLoadingSpinner';
import {CustomMenuItem} from '../common/arr-menu/ArrMenu';
import ArrModal from '../common/arr-modal/ArrModal';
import ArrSingleSelectDropdown from '../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTextField from '../common/arr-text-field/ArrTextField';
import ArrToastNotification from '../common/arr-toast-notification/ArrToastNotification';
import {ArrToggle} from '../common/arr-toggle/ArrToggle';
import ArrTooltipWrapper from '../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrTreeView from '../common/arr-tree-view/ArrTreeView';
import {
  buildTree,
  findNodeById,
  FOLDERS_ACTION_MENU,
  TAGS_ACTION_MENU,
} from './tagsAndFoldersUtils';
import './TagsFoldersTabContent.scss';
import {getParentIds} from './utils';

interface ITagsFoldersTabContentProps {
  type: 'tags' | 'folder';
}

const FolderActions = {
  EDIT: 'edit',
  ADD: 'add',
  ADD_GROUP: 'add_group',
  NULL: null,
} as const;
type FolderActionType = (typeof FolderActions)[keyof typeof FolderActions];
enum TagType {
  add = 'add',
  edit = 'edit',
  addTag = 'add_tag',
  editTag = 'edit_tag',
}

const initialSelectedSecurityGroup: ISelectItem = {
  securityGroupID: '',
  securityGroupName: t('tagsAndFolders.allSecurityGroups'),
};

const MAX_NAME_LENGTH = 255;

const TagsFoldersTabContent = ({type}: ITagsFoldersTabContentProps) => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const lastEvent = useSelector((state: RootState) => state.signalr.lastEvent);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isToggleActive, setIsToggleActive] = useState(false);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [selectedItemObj, setSelectedItemObj] = useState<any>();
  const [selectedSecurityGroup, setSelectedSecurityGroup] =
    useState<ISelectItem | null>(
      loginUserData.roleId === 1 ? initialSelectedSecurityGroup : null
    );
  const [showAddUpdateTagGroupModal, setShowAddUpdateTagGroupModal] =
    useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteModalDescription, setDeleteModalDescription] =
    useState<string>('');
  const [tagOperation, setTagOperation] = useState<TagType | null>(null);
  const [tagGroupName, setTagGroupName] = useState<string>('');
  const [tagGroupNameError, setTagGroupNameError] = useState<string>('');
  const [folderOperation, setFolderOperation] = useState<FolderActionType>(
    FolderActions.NULL
  );
  const [folderGroupName, setFolderGroupName] = useState<string>('');
  const [folderGroupNameError, setFolderGroupNameError] = useState<string>('');
  const [originalFolderGroupName, setOriginalFolderGroupName] =
    useState<string>('');
  const [selectedFolderOrFolderGroup, setSelectedFolderOrFolderGroup] =
    useState<ITagsList | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType>(ToastType.Success);
  const [currentDepth, setCurrentDepth] = useState<number>(0);
  const [originalTagGroupName, setOriginalTagGroupName] = useState<string>('');
  const [familyMembers, setFamilyMembers] = useState<boolean>(false);
  const [exactDuplicates, setExactDuplicates] = useState<boolean>(false);
  const [emailThreads, setEmailThreads] = useState<boolean>(false);
  const [emailDuplicates, setEmailDuplicates] = useState<boolean>(false);
  const [classificationList, setClassificationList] = useState<
    IclassificationList[]
  >([]);
  const [selectedClassification, setSelectedClassification] =
    useState<IclassificationList | null>();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<string[]>(['-1']);
  const initialExpandedIdsRef = useRef<string[]>(expandedIds);
  const [folderName, setFolderName] = useState<string>('');
  const [folderNameError, setFolderNameError] = useState<string>('');
  const [originalFolderName, setOriginalFolderName] = useState<string>('');
  const [showFolderEditModal, setShowFolderEditModal] =
    useState<boolean>(false);
  const [selectedItemsCount, setSelectedItemsCount] = useState<number>(0);
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [docCount, setDocCount] = useState<number>(0);
  const [showDocLimitWarning, setShowDocLimitWarning] =
    useState<boolean>(false);
  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>(SourceTypeCode.DOCVIEW);
  const [docListMasterId, setDocListMasterId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);

  const {mutate: mutateAddTagGroup} = useAddTagGroup();
  const {mutate: mutateAddTag} = useEditTagOrTagGroup();
  const {mutate: moveFolderOrFolderGroup} = useMoveFolderOrFolderGroup();
  const {mutate: moveTagOrTagGroup} = useMoveTagOrTagGroup();
  const {mutate: mutateAddFolder} = useAddFolderGroup();
  const {mutate: mutateGetDocumentForTagAndFolder} =
    useGetDocumentForTagAndFolder((response) => {
      if (response?.docCount === 0) {
        triggerToast(
          t('tagsAndFolders.tagsFoldersNoDocument'),
          ToastType.Warning
        );
        return;
      }
      if (selectedAction === 'view_in_docList') {
        setDocCount(response.docCount);
        setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
        setShowDocLimitWarning(true);
      } else if (
        selectedAction === 'view_in_docView' ||
        selectedAction === 'view_in_docView_in_new_tab'
      ) {
        setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
        setDocCount(response.docCount);
        setShowDocLimitWarning(true);
      }
    });
  const {mutate: getNavigationMasterListId} = useGetNavigationMasterListId();
  const {mutate: addTag, isPending: isAddTagLoading} = useAddTag();

  const handleActionToggle = (status: boolean) => {
    setIsToggleActive(status);
  };

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setSearchKeyword(searchInput.trim());
      setSelectedItem('');
      setSelectedItemsCount(0);
    }
  };

  const {data: viewData, isLoading: isLoadingViewData} =
    useGetTagsFoldersViewData();

  const securityGroupList = useMemo(() => {
    const list = viewData?.securityGroupList || [];
    return loginUserData.roleId === 1
      ? [initialSelectedSecurityGroup, ...list]
      : list;
  }, [viewData, loginUserData.roleId]);

  const queryParams = useMemo(() => {
    const toggleValue = isToggleActive ? 1 : 0;

    const securityroupId = selectedSecurityGroup?.securityGroupID || 0;

    return `?securityGroupId=${securityroupId}&toggelValue=${toggleValue}`;
  }, [type, selectedSecurityGroup?.securityGroupID, isToggleActive]);

  const shouldFetchTags = type === 'tags' && !!selectedSecurityGroup;

  const shouldFetchFolders = type === 'folder' && !!selectedSecurityGroup;
  const {mutate: mutateDeleteTagGroupOrTag} = useDeleteTagGroupOrTag();
  const {mutate: mutateDeleteFolderGroupOrFolder} =
    useDeleteFolderGroupOrFolder();

  const {
    data: foldersList,
    isLoading: isLoadingFoldersList,
    refetch: refetchGetFolderList,
  } = useGetFoldersList(queryParams, shouldFetchFolders);
  const {
    data: tagsList,
    isLoading: isLoadingTagsList,
    refetch: refetchGetTagList,
  } = useGetTagsList(queryParams, shouldFetchTags);

  const editTagQueryParams = useMemo(
    () =>
      `?securityGroupId=${selectedSecurityGroup?.securityGroupID || 0}&tagId=${selectedItem || 0}`,
    [selectedSecurityGroup?.securityGroupID, selectedItem]
  );

  const {data: editTagData, refetch: refetchGetEditTagorTagGroup} =
    useGetEditTagOrTagGroup(editTagQueryParams, !!selectedItem);

  const isGroupTag = useMemo(
    () => tagOperation === TagType.add || tagOperation === TagType.edit,
    [tagOperation]
  );

  const isEditTagOperation = useMemo(
    () => tagOperation === TagType.editTag || tagOperation === TagType.edit,
    [tagOperation]
  );

  const getTreeDataList =
    useMemo((): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
      return type === 'tags'
        ? buildTree(tagsList?.tagList || [])
        : buildTree(foldersList || []);
    }, [tagsList, type, foldersList]);

  useEffect(() => {
    if (loginUserData.roleId !== 1 && securityGroupList.length > 0) {
      setSelectedSecurityGroup(securityGroupList[0] as ISelectItem);
    }
  }, [loginUserData.roleId, securityGroupList]);

  useEffect(() => {
    setClassificationList(viewData?.classificationList ?? []);
  }, [viewData?.classificationList]);
  const getAllParentIds = (
    tree: TreeViewBaseItem<ExtendedTreeItemProps>[],
    targetId: string
  ): string[] => {
    const result: string[] = [];
    let currentNode = findNodeById(tree, targetId);

    while (currentNode && currentNode?.parent) {
      result.push(String(currentNode?.parent));
      currentNode = findNodeById(tree, String(currentNode?.parent));
    }

    return result;
  };

  const moveFolderOrFolderGroupAsync = (payload: any) => {
    return new Promise<void>((resolve, reject) => {
      moveFolderOrFolderGroup(payload, {
        onSuccess: () => resolve(),
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
          reject(error);
        },
      });
    });
  };

  const moveTagOrTagGroupAsync = (payload: any) => {
    return new Promise<void>((resolve, reject) => {
      moveTagOrTagGroup(payload, {
        onSuccess: () => resolve(),
        onError: (error) => reject(error),
      });
    });
  };

  const resetModalTagState = () => {
    setTagGroupName('');
    setTagGroupNameError('');
    setOriginalTagGroupName('');
    setFamilyMembers(false);
    setExactDuplicates(false);
    setEmailThreads(false);
    setEmailDuplicates(false);
    setSelectedClassification(null);
  };

  const {mutate: mutateAddFolderGroup, isPending: folderGroupMutateState} =
    useAddFolderGroup();

  const enableFetchFolderOrFolderData =
    !_.isEmpty(selectedItem) && !_.isNull(selectedFolderOrFolderGroup);

  const {data: currentFolderItem} = useGetFolderOrFolderGroup(
    {
      securityGroupID:
        selectedFolderOrFolderGroup?.saveSearchSecurityGroupId !== undefined
          ? Number(selectedFolderOrFolderGroup?.saveSearchSecurityGroupId)
          : 0,
      tagId: Number(selectedItem),
    },
    enableFetchFolderOrFolderData
  );

  const {
    mutate: mutateUpdateFolderOrFolderGroup,
    isPending: updateFolderState,
  } = useUpdateFolderOrFolderGroup();

  const handleActionMenuItemClick = (e: CustomMenuItem) => {
    setSelectedAction(e.id);
    const idsToSend =
      selectedItemIds.length > 0
        ? selectedItemIds
        : selectedItem
          ? [selectedItem]
          : [];
    if (e.id === 'view_in_docList') {
      const payload = {
        sourceInfo: {
          sourceDetails: idsToSend.map((id) => ({
            sourceID: Number(id),
          })),
          sourceTypeCode: type === 'tags' ? 'TAG' : 'FOLDER',
          destinationContextTypeCode: 'DOCLIST',
          isDocumentCount: false,
        },
      };
      mutateGetDocumentForTagAndFolder(payload);
      return;
    } else if (
      e.id === 'view_in_docView' ||
      e.id === 'view_in_docView_in_new_tab'
    ) {
      const payload = {
        sourceInfo: {
          sourceDetails: idsToSend.map((id) => ({
            sourceID: Number(id),
          })),
          sourceTypeCode: type === 'tags' ? 'TAG' : 'FOLDER',
          destinationContextTypeCode: 'DOCVIEW',
          isDocumentCount: false,
        },
      };
      mutateGetDocumentForTagAndFolder(payload);
    }
    const treeData = getTreeDataList || [];
    const node = findNodeById(treeData, selectedItem);
    setSelectedItemObj(node);
    if (type === 'tags') {
      if (e.id === 'new_tag_group') {
        if (currentDepth >= 5) {
          triggerToast(
            t('tagsAndFolders.tagGroupLevelValidation'),
            ToastType.Warning
          );
          return;
        }
        setTagOperation(TagType.add);
        setTagGroupName('');
        setTagGroupNameError('');
        setOriginalTagGroupName('');
        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'edit' && node?.type === 'tags') {
        setTagOperation(TagType.edit);
        setTagGroupNameError('');
        const editTag = tagsList?.tagList.find(
          (e) => String(e.id) === String(selectedItem)
        );
        const currentName =
          (editTag?.textWithoutCount ||
            editTag?.text.replace(/(?=(\s*))\1\(\d+\)$/, '')) ??
          '';
        setTagGroupName(currentName);
        setOriginalTagGroupName(currentName);
        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'new_tag') {
        setTagOperation(TagType.addTag);
        resetModalTagState();
        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'edit' && node?.type === 'tag') {
        setTagOperation(TagType.editTag);
        setTagGroupNameError('');
        setTagGroupName(editTagData?.name ?? '');
        setOriginalTagGroupName(editTagData?.name ?? '');
        setFamilyMembers(editTagData?.propagationToFamily ?? false);
        setExactDuplicates(editTagData?.propagationToDuplicates ?? false);
        setEmailThreads(editTagData?.propagationToEmailThreads ?? false);
        setEmailDuplicates(editTagData?.propagationToEmailDuplicates ?? false);
        setShowAddUpdateTagGroupModal(true);
        setSelectedClassification(
          classificationList?.find(
            (item) => item.key === editTagData?.classificationID
          )
        );
      } else if (e.id === 'delete') {
        setShowDeleteModal(true);
        if (node?.type === 'tag' || node?.type === 'tags') {
          setDeleteModalDescription(
            t(
              `tagsAndFolders.modal.delete.${node?.isGroup ? 'deleteTagGroupDescription' : 'deleteTagDescription'}`
            ) +
              ' ' +
              node?.label +
              `${node?.isGroup ? ' ?' : ''}`
          );
        }
      }
    } else if (type === 'folder') {
      if (e.id === 'new_folder_group') {
        if (currentDepth > 5) {
          triggerToast(
            t('tagsAndFolders.folderGroupLevelValidation'),
            ToastType.Warning
          );
          return;
        }
        resetModalFolderState();
        setFolderOperation(FolderActions.ADD_GROUP);
        setFolderGroupName('');
        setFolderGroupNameError('');
        setOriginalFolderGroupName('');
        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'new_folder') {
        setFolderName('');
        setFolderNameError('');
        resetModalFolderState();
        setFolderOperation(FolderActions.ADD);
        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'edit') {
        setFolderOperation(FolderActions.EDIT);
        setFolderGroupNameError('');
        setFolderNameError('');
        setFamilyMembers(
          currentFolderItem?.editFolderOrFolderGroupData?.propagationToFamily ??
            false
        );
        setExactDuplicates(
          currentFolderItem?.editFolderOrFolderGroupData
            ?.propagationToDuplicates ?? false
        );
        setEmailThreads(
          currentFolderItem?.editFolderOrFolderGroupData
            ?.propagationToEmailThreads ?? false
        );
        setEmailDuplicates(
          currentFolderItem?.editFolderOrFolderGroupData
            ?.propagationToEmailDuplicates ?? false
        );

        const currentFolder =
          foldersList?.find((e) => String(e.id) === String(selectedItem)) ??
          null;

        if (currentFolder) {
          const folderWithCorrectSecurityGroup = {
            ...currentFolder,
            saveSearchSecurityGroupId: Number(
              selectedSecurityGroup?.securityGroupID !== undefined
                ? selectedSecurityGroup?.securityGroupID
                : currentFolder.saveSearchSecurityGroupId
            ),
          };
          setSelectedFolderOrFolderGroup(folderWithCorrectSecurityGroup);
        } else {
          setSelectedFolderOrFolderGroup(null);
        }
        const currentFolderName = currentFolder?.textWithoutCount ?? '';
        if (node?.isGroup) {
          setFolderGroupName(currentFolderName);
          setOriginalFolderGroupName(currentFolderName);
        } else {
          setFolderName(currentFolderName);
          setOriginalFolderName(currentFolderName);
        }

        setShowAddUpdateTagGroupModal(true);
      } else if (e.id === 'delete') {
        setShowDeleteModal(true);
        setDeleteModalDescription(
          t(
            `tagsAndFolders.modal.delete.${node?.isGroup ? 'deleteFolderGroupDescription' : 'deleteFolderDescription'}`
          ) +
            ' ' +
            node?.label +
            `${node?.isGroup ? ' ?' : ''}`
        );
      }
    }
  };

  const isSaveDisabled = useMemo(() => {
    if (type === 'tags') {
      const trimmed = tagGroupName.trim();
      if (trimmed.length === 0) return true;
      if (tagGroupNameError) return true;
      if (tagOperation === 'edit') {
        return trimmed === originalTagGroupName.trim();
      }
    } else if (type == 'folder') {
      if (folderOperation === FolderActions.ADD_GROUP) {
        const trimmed = folderGroupName.trim();
        if (_.isEmpty(trimmed)) return true;
        if (folderGroupNameError) return true;
        return false;
      } else if (folderOperation === FolderActions.ADD) {
        const trimmed = folderName.trim();
        if (_.isEmpty(trimmed)) return true;
        if (folderNameError) return true;
        return false;
      } else if (folderOperation === FolderActions.EDIT) {
        if (selectedItemObj?.isGroup) {
          const trimmed = folderGroupName.trim();
          if (_.isEmpty(trimmed)) return true;
          if (folderGroupNameError) return true;
          return _.isEqual(trimmed, originalFolderGroupName.trim());
        } else {
          const trimmed = folderName.trim();
          if (_.isEmpty(trimmed)) return true;
          if (folderNameError) return true;

          const nameChanged = !_.isEqual(trimmed, originalFolderName.trim());

          const propagationChanged =
            familyMembers !==
              (currentFolderItem?.editFolderOrFolderGroupData
                ?.propagationToFamily ?? false) ||
            exactDuplicates !==
              (currentFolderItem?.editFolderOrFolderGroupData
                ?.propagationToDuplicates ?? false) ||
            emailThreads !==
              (currentFolderItem?.editFolderOrFolderGroupData
                ?.propagationToEmailThreads ?? false) ||
            emailDuplicates !==
              (currentFolderItem?.editFolderOrFolderGroupData
                ?.propagationToEmailDuplicates ?? false);

          return !nameChanged && !propagationChanged;
        }
      }
    }
    return false;
  }, [
    tagGroupName,
    tagOperation,
    originalTagGroupName,
    tagGroupNameError,
    folderGroupName,
    folderGroupNameError,
    originalFolderGroupName,
    folderName,
    folderNameError,
    originalFolderName,
    folderOperation,
    selectedItemObj?.isGroup,
    selectedItem,
    type,
    familyMembers,
    exactDuplicates,
    emailThreads,
    emailDuplicates,
    currentFolderItem?.editFolderOrFolderGroupData,
  ]);

  const triggerToast = (message: string, status: ToastType) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const validateName = (name: string): string => {
    const trimmed = name.trim();
    if (trimmed.length === 0) {
      return t('tagsAndFolders.validation.nameRequired');
    }
    if (name.length > MAX_NAME_LENGTH) {
      return t('tagsAndFolders.validation.maxLength', {max: MAX_NAME_LENGTH});
    }
    if (!TAG_NAME_REGEX.test(name)) {
      return t('tagsAndFolders.validation.specialCharacters');
    }
    return '';
  };

  const addEditTagGroupApiCall = () => {
    const payload = {
      id: isEditTagOperation ? selectedItemObj?.id : 0,
      securityGroupID: selectedSecurityGroup?.securityGroupID || 0,
      parentID: isEditTagOperation
        ? selectedItemObj?.parent
        : Number(selectedItem),
      name: tagGroupName,
      isGroup: isGroupTag,
      propagationToFamily: familyMembers,
      propagationToDuplicates: exactDuplicates,
      propagationToEmailThreads: emailThreads,
      propagationToEmailDuplicates: emailDuplicates,
      classificationID: selectedClassification?.key ?? 0,
      classification: selectedClassification?.value ?? '',
    };
    if (isGroupTag) {
      mutateAddTagGroup(payload, {
        onSuccess: () => {
          triggerToast(
            isEditTagOperation
              ? t('tagsAndFolders.tagGroupEdited')
              : t('tagsAndFolders.tagGroupAdded'),
            ToastType.Success
          );
          setShowAddUpdateTagGroupModal(false);
          setTagGroupName('');
          setTagGroupNameError('');
          setOriginalTagGroupName('');
          refetchGetTagList();
        },
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
        },
      });
    } else {
      if (tagOperation === 'add_tag') {
        addTag(
          {createNewCalModel: false, tagData: payload},
          {
            onSuccess: (res) => {
              if (res?.errorText?.includes(existingObjErrCode)) {
                triggerToast(
                  t('tagsAndFolders.modal.errorSameName'),
                  ToastType.Error
                );
              } else {
                triggerToast(
                  t('tagsAndFolders.modal.addTagSuccess'),
                  ToastType.Success
                );
              }
              setShowAddUpdateTagGroupModal(false);
              setShowEditModal(false);
              resetModalTagState();
              setTagOperation(null);
              refetchGetTagList();
            },
            onError: (error) => {
              triggerToast(error.message, ToastType.Error);
              setShowAddUpdateTagGroupModal(false);
              setShowEditModal(false);
            },
          }
        );
      } else {
        mutateAddTag(
          {tagData: payload},
          {
            onSuccess: () => {
              triggerToast(
                isEditTagOperation
                  ? t('tagsAndFolders.modal.editTagSuccess')
                  : t('tagsAndFolders.modal.addTagSuccess'),
                ToastType.Success
              );
              setShowAddUpdateTagGroupModal(false);
              setShowEditModal(false);
              resetModalTagState();
              setTagOperation(null);
              refetchGetTagList();
              refetchGetEditTagorTagGroup();
            },
            onError: () => {
              triggerToast(
                t('tagsAndFolders.modal.errorSameName'),
                ToastType.Error
              );
              setShowAddUpdateTagGroupModal(false);
              setShowEditModal(false);
            },
          }
        );
      }
    }
  };

  const addFolderGroupApiCall = () => {
    const payload: IAddFolderGroupRequest = {
      securityGroupID:
        selectedSecurityGroup!.securityGroupID !== ''
          ? Number(selectedSecurityGroup!.securityGroupID)
          : 0,
      projectID: 0,
      parentID: Number(selectedItem),
      name: folderGroupName,
      isGroup: true,
      propagationToFamily: true,
      propagationToDuplicates: true,
      propagationToEmailThreads: true,
      propagationToEmailDuplicates: true,
      isLocked: true,
      masterDocListID: 0,
      count: 0,
    };

    mutateAddFolderGroup(payload, {
      onSuccess: () => {
        triggerToast(
          t('tagsAndFolders.modal.folderGroupAdded'),
          ToastType.Success
        );
        setShowAddUpdateTagGroupModal(false);
        setFolderGroupName('');
        setFolderOperation(FolderActions.NULL);
        setFolderGroupNameError('');
        setFolderNameError('');
        setOriginalFolderGroupName('');
        refetchGetFolderList();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  const resetModalFolderState = () => {
    setFolderName('');
    setFolderNameError('');
    setOriginalFolderName('');
    setFolderGroupName('');
    setFolderGroupNameError('');
    setOriginalFolderGroupName('');
    setSelectedFolderOrFolderGroup(null);
    setFamilyMembers(false);
    setExactDuplicates(false);
    setEmailThreads(false);
    setEmailDuplicates(false);
  };

  const addFolderApiCall = () => {
    const payload: IAddFolderGroupRequest = {
      securityGroupID:
        selectedSecurityGroup!.securityGroupID !== ''
          ? Number(selectedSecurityGroup!.securityGroupID)
          : 0,
      projectID: loginUserData?.projectId || 0,
      parentID: Number(selectedItem),
      name: folderName,
      isGroup: false,
      propagationToFamily: familyMembers,
      propagationToDuplicates: exactDuplicates,
      propagationToEmailThreads: emailThreads,
      propagationToEmailDuplicates: emailDuplicates,
      isLocked: true,
      masterDocListID: 0,
      count: 0,
    };

    mutateAddFolder(payload, {
      onSuccess: () => {
        triggerToast(
          t('tagsAndFolders.modal.addFolderSuccess'),
          ToastType.Success
        );
        setShowAddUpdateTagGroupModal(false);
        resetModalFolderState();
        setFolderOperation(FolderActions.NULL);
        refetchGetFolderList();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
      },
    });
  };

  const updateFolderOrFolderGroupApiCall = () => {
    setShowAddUpdateTagGroupModal(false);
    const payload = {
      ...currentFolderItem!.editFolderOrFolderGroupData,
      name: folderGroupName,
      id: Number(selectedItem),
      ...(selectedItemObj?.isGroup
        ? {}
        : {
            propagationToFamily: familyMembers,
            propagationToDuplicates: exactDuplicates,
            propagationToEmailThreads: emailThreads,
            propagationToEmailDuplicates: emailDuplicates,
          }),
    };
    mutateUpdateFolderOrFolderGroup(payload, {
      onSuccess: () => {
        triggerToast(
          t('tagsAndFolders.modal.folderGroupUpdated'),
          ToastType.Success
        );
        setShowAddUpdateTagGroupModal(false);
        setFolderGroupName('');
        setFolderName('');
        setFolderOperation(FolderActions.NULL);
        setFolderGroupNameError('');
        setFolderNameError('');
        setOriginalFolderGroupName('');
        setOriginalFolderName('');
        resetModalFolderState();
        refetchGetFolderList();
        setSelectedFolderOrFolderGroup(null);
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        setFolderGroupName('');
        setFolderName('');
        setFolderOperation(FolderActions.NULL);
        resetModalFolderState();
      },
    });
  };
  const updateFolderApiCall = () => {
    if (!currentFolderItem) return;
    setShowAddUpdateTagGroupModal(false);
    setShowFolderEditModal(false);

    const payload = {
      ...currentFolderItem.editFolderOrFolderGroupData,
      name: folderName.trim(),
      id: Number(selectedItem),
      propagationToFamily: familyMembers,
      propagationToDuplicates: exactDuplicates,
      propagationToEmailThreads: emailThreads,
      propagationToEmailDuplicates: emailDuplicates,
    };

    mutateUpdateFolderOrFolderGroup(payload, {
      onSuccess: () => {
        triggerToast(
          t('tagsAndFolders.modal.folderUpdated'),
          ToastType.Success
        );
        setShowAddUpdateTagGroupModal(false);
        setShowFolderEditModal(false);
        resetModalFolderState();
        setFolderOperation(FolderActions.NULL);
        refetchGetFolderList();
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        resetModalFolderState();
        setFolderOperation(FolderActions.NULL);
      },
    });
  };
  const handleEditFolderConfirm = () => {
    if (
      folderOperation === FolderActions.EDIT &&
      selectedItemObj?.type === 'folder'
    ) {
      const trimmedName = folderName.trim();
      if (trimmedName !== originalFolderName.trim()) {
        setShowFolderEditModal(true);
        return;
      }
    }
    updateFolderApiCall();
  };

  const handleOnSaveEvent = () => {
    if (type === 'tags') {
      const treeData = type === 'tags' ? getTreeDataList : [];
      const node = findNodeById(treeData, selectedItem);
      if (tagOperation === TagType.editTag && node?.type === 'tag') {
        setShowEditModal(true);
        return;
      }
      if (tagOperation) {
        addEditTagGroupApiCall();
      }
    } else if (type === 'folder') {
      const treeData = getTreeDataList || [];
      const node = findNodeById(treeData, selectedItem);
      if (folderOperation === FolderActions.ADD_GROUP) {
        addFolderGroupApiCall();
      } else if (folderOperation === FolderActions.ADD) {
        addFolderApiCall();
      } else if (folderOperation === FolderActions.EDIT) {
        if (node?.type === 'folder') {
          handleEditFolderConfirm();
        } else {
          updateFolderOrFolderGroupApiCall();
        }
      }
    }
  };

  const handleMoveFolderOrFolderGroup = async (
    source: TreeViewBaseItem<ExtendedTreeItemProps>,
    target: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (!source?.data?.length || !target?.id) return;

    if (target.type === 'folder') {
      const isFolderGroup = source.data.some((item) => item.type === 'folders');
      if (isFolderGroup) {
        triggerToast(
          t('tagsAndFolders.folderGroupIntoFolder'),
          ToastType.Warning
        );
      } else {
        triggerToast(t('tagsAndFolders.folderIntoFolder'), ToastType.Warning);
      }
      return;
    }

    try {
      for (const item of source.data) {
        const payload = {
          id: item.id,
          securityGroupID: item.saveSearchSecurityGroupId,
          projectID: 0,
          parentID: target.id,
          name: item.name,
          isGroup: item.isGroup,
          count: item.count,
          propagationToFamily: true,
          propagationToDuplicates: true,
          propagationToEmailThreads: true,
          propagationToEmailDuplicates: true,
          isLocked: false,
          masterDocListID: 0,
        };

        await moveFolderOrFolderGroupAsync(payload);
      }
      await refetchGetFolderList();

      triggerToast(t('tagsAndFolders.folderMoveSuccess'), ToastType.Success);
    } catch (error: any) {
      triggerToast(error.message, ToastType.Error);
    }
  };

  const handleMoveTagOrTagGroup = async (
    source: TreeViewBaseItem<ExtendedTreeItemProps>,
    target: TreeViewBaseItem<ExtendedTreeItemProps>
  ) => {
    if (!source?.data?.length || !target?.id) return;

    if (target.type === 'tag') {
      let message;
      for (const src of source.data) {
        message =
          src.type == 'tag'
            ? t('tagsAndFolders.tagToTag')
            : t('tagsAndFolders.tagGroupToTag');
        triggerToast(message, ToastType.Warning);
        return;
      }
    }

    const hasNoAccess =
      loginUserData?.roleId === 2 ||
      source.data.some(
        (item) =>
          item.saveSearchSecurityGroupId !==
          Number(selectedSecurityGroup?.securityGroupID)
      );

    if (hasNoAccess) {
      triggerToast(t('tagsAndFolders.tagGroupMovedError'), ToastType.Error);
      return;
    }

    try {
      for (const tagItem of source.data) {
        const payload: IMoveTagOrTagGroupPayload = {
          id: Number(tagItem.id),
          securityGroupID: Number(tagItem.saveSearchSecurityGroupId),
          projectID: 0,
          parentID: Number(target.id),
          name: tagItem.label,
          isGroup: Boolean(tagItem.isGroup),
          count: Number(tagItem.count) || 0,
          propagationToFamily: true,
          propagationToDuplicates: true,
          propagationToEmailThreads: true,
          propagationToEmailDuplicates: true,
          isLocked: true,
          masterDocListID: 0,
          classificationID: 0,
          classification: '',
          isSelected: true,
          isAIEnabled: true,
          showAIHints: true,
          calModelID: 0,
        };
        await moveTagOrTagGroupAsync(payload);

        const tagItemType =
          tagItem.type == 'tag' ? 'tagMoved' : 'tagGroupMoved';
        triggerToast(t(`tagsAndFolders.${tagItemType}`), ToastType.Success);
      }

      await refetchGetTagList();
    } catch (error: any) {
      triggerToast(error.message, ToastType.Error);
    }
  };

  const actionMenuItems = useMemo(() => {
    const menuOptions =
      type === 'tags' ? TAGS_ACTION_MENU : FOLDERS_ACTION_MENU;
    const isMultiSelect = selectedItemsCount > 1;

    const CREATE_IDS = [
      'new_tag',
      'new_tag_group',
      'new_folder',
      'new_folder_group',
    ];

    const isCreateId = (id: string) => CREATE_IDS.includes(id);

    const disableViewSubmenu = (item: any) => {
      if (item.id !== 'view' || !Array.isArray(item.submenu)) return item;

      return {
        ...item,
        disabled: false,
        submenu: item.submenu.map((s: CustomMenuItem) => ({
          ...s,
          disabled: true,
        })),
      };
    };

    const mapMenu = (ruleFn: (id: string) => boolean, disableView: boolean) =>
      menuOptions.map((item) => {
        let disabled = !ruleFn(item.id);

        // Disable edit/delete on multi-select
        if (isMultiSelect && (item.id === 'edit' || item.id === 'delete')) {
          disabled = true;
        }

        // Disable bulk release for Review Manager
        if (
          item.id === ReportActionMenuOptionTypes.BulkRelease &&
          loginUserData?.roleId === 2
        ) {
          disabled = true;
        }

        if (item.id === 'view') {
          return disableView ? disableViewSubmenu(item) : {...item, disabled};
        }
        return {...item, disabled};
      });

    if (!selectedItem) {
      return mapMenu((id) => id === 'view', true);
    }

    const treeData = getTreeDataList;
    const node = findNodeById(treeData, selectedItem);

    if (!node) return menuOptions;

    const isRoot = node.id === '-1';
    const isGroup = node.isGroup;
    const hasChildren = !!node.children?.length;

    if (isRoot) {
      return mapMenu((id) => isCreateId(id), true);
    }

    if (isGroup) {
      return mapMenu((id) => {
        if (isCreateId(id)) return true;
        if (id === 'edit') return true;
        if (id === 'delete' && !hasChildren) return true;
        return false;
      }, true);
    }

    return mapMenu((id) => !isCreateId(id), false);
  }, [type, selectedItem, selectedItemsCount, loginUserData?.roleId]);

  const handleSelected = (value: string[] | null) => {
    if (Array.isArray(value) && value?.length > 0) {
      setSelectedItemsCount(value?.length);
      setSelectedItem(value[0]);
      setSelectedItemIds(value);
      const parentIds = getParentIds(getTreeDataList, value[0]);
      if (parentIds) {
        initialExpandedIdsRef.current = parentIds;
      }
    } else {
      setSelectedItemsCount(0);
      setSelectedItem('');
      setSelectedItemIds([]);
      setDocCount(0);
    }
  };

  const modalProps = (type: string) => {
    const commonProps = {
      showModal: showAddUpdateTagGroupModal,
      onClose: () => setShowAddUpdateTagGroupModal(false),
      className: 'tag-group-modal',
      dataTestId: 'arr-modal-tag',
      'aria-labelledby': 'arr-modal-tag-title',
      'aria-describedby': 'arr-modal-tag-description',
    };
    if (type === 'tags') {
      const tagsProps = {
        ...commonProps,
        buttons: [
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setShowAddUpdateTagGroupModal(false);
              setTagGroupName('');
              setTagGroupNameError('');
              setOriginalTagGroupName('');
              resetModalTagState();
            },
            variant: 'secondary',
            dataTestId: 'tag-modal-cancel-btn',
          },
          {
            id: '1',
            label: t('common.save'),
            onClick: handleOnSaveEvent,
            variant: 'primary',
            dataTestId: 'tag-modal-save-btn',
            disabled: isSaveDisabled,
          },
        ],
      };
      return tagsProps;
    } else if (type === 'folder') {
      const folderModalProps = {
        ...commonProps,
        buttons: [
          {
            id: '1',
            label: t('common.cancel'),
            onClick: () => {
              setShowAddUpdateTagGroupModal(false);
              setFolderGroupName('');
              setFolderGroupNameError('');
              setOriginalFolderGroupName('');
            },
            variant: 'secondary',
            dataTestId: 'folder-modal-cancel-btn',
          },
          {
            id: '1',
            label: t('common.save'),
            onClick: handleOnSaveEvent,
            variant: 'primary',
            dataTestId: 'folder-modal-save-btn',
            disabled:
              isSaveDisabled || folderGroupMutateState || updateFolderState,
          },
        ],
      };
      return folderModalProps;
    }
    return commonProps;
  };

  const handleDeleteModal = () => {
    if (!selectedItemObj) return;
    if (selectedItemObj?.type === 'tag' || selectedItemObj?.type === 'tags') {
      const payload = {
        id: Number(selectedItemObj?.id),
        securityGroupID: Number(selectedSecurityGroup?.securityGroupID) || 0,
        projectID: loginUserData?.projectId,
        parentID: Number(selectedItemObj?.parent),
        name: selectedItemObj?.text,
        isGroup: selectedItemObj?.isGroup,
        count: selectedItemObj?.documentCount,
        propagationToFamily: false,
        propagationToDuplicates: false,
        propagationToEmailThreads: false,
        propagationToEmailDuplicates: false,
        isLocked: false,
        masterDocListID: 0,
        classificationID: 0,
        classification: '',
        isSelected: selectedItemObj?.isSelected,
        isAIEnabled: false,
        showAIHints: false,
        calModelID: 0,
      };

      mutateDeleteTagGroupOrTag(payload, {
        onSuccess: () => {
          triggerToast(
            t(
              `tagsAndFolders.modal.delete.${selectedItemObj?.isGroup ? 'deleteTagGroupSuccess' : 'deleteTagSuccess'}`
            ),
            ToastType.Success
          );
          setShowDeleteModal(false);
          refetchGetTagList();
        },
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
        },
      });
    } else if (
      selectedItemObj?.type === 'folder' ||
      selectedItemObj?.type === 'folders'
    ) {
      const payload = {
        tagId: Number(selectedItemObj?.id),
        securityGroupID: Number(selectedSecurityGroup?.securityGroupID) || 0,
      };
      mutateDeleteFolderGroupOrFolder(payload, {
        onSuccess: () => {
          triggerToast(
            t(
              `tagsAndFolders.modal.delete.${selectedItemObj?.isGroup ? 'deleteFolderGroupSuccess' : 'deleteFolderSuccess'}`
            ),
            ToastType.Success
          );
          setShowDeleteModal(false);
          refetchGetFolderList();
        },
        onError: (error) => {
          triggerToast(error.message, ToastType.Error);
        },
      });
    }
  };

  useEffect(() => {
    if (
      folderOperation === FolderActions.EDIT &&
      currentFolderItem?.editFolderOrFolderGroupData &&
      !selectedItemObj?.isGroup
    ) {
      const data = currentFolderItem.editFolderOrFolderGroupData;
      setFamilyMembers(data.propagationToFamily ?? false);
      setExactDuplicates(data.propagationToDuplicates ?? false);
      setEmailThreads(data.propagationToEmailThreads ?? false);
      setEmailDuplicates(data.propagationToEmailDuplicates ?? false);
    }
  }, [
    currentFolderItem?.editFolderOrFolderGroupData,
    folderOperation,
    selectedItemObj?.isGroup,
  ]);

  const modalContent = (type: string) => {
    let content;
    if (type === 'tags') {
      content = (
        <>
          <Typography
            id="arr-modal-tag-title"
            className="typo-text headline3_size tag-group-title"
            data-testid={`tags-folders-tab-content-tag-title`}
          >
            {tagTitle}
          </Typography>
          <Box className="text-field-area">
            <ArrTextField
              id="tbt-tag-name-input"
              name="tagName"
              className="option-number"
              sizeVariant="large"
              value={tagGroupName}
              onChange={(e) => {
                setTagGroupNameError(validateName(e.target.value));
                setTagGroupName(e.target.value);
              }}
              showErrorIcon={Boolean(tagGroupNameError)}
              state={tagGroupNameError ? 'error' : 'enabled'}
              label={t('tagsAndFolders.modal.nameAstric')}
              supportText={tagGroupNameError}
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                  'aria-label': t('tagsAndFolders.modal.nameAstric'),
                },
              }}
              dataTestId="tbt-tag-name-input"
            />
            {isGroupTag ? (
              <Typography
                id="arr-modal-tag-description"
                className="typo-text body4_size text-field-label"
                data-testid={`tags-folders-tab-content-tag-modal-label`}
              >
                {t('tagsAndFolders.tagModalLabel')}
              </Typography>
            ) : (
              <Box>
                <Box className="dropdown-container">
                  <ArrSingleSelectDropdown
                    infoIcontooltip={t(
                      'tagsAndFolders.modal.tagClassificationTooltipDesc'
                    )}
                    selectedValues={
                      selectedClassification
                        ? {
                            key: String(selectedClassification.key),
                            value: selectedClassification.value,
                          }
                        : null
                    }
                    dropdownLabel={t('tagsAndFolders.modal.tagClassification')}
                    dropdownItems={classificationList?.map((item) => ({
                      key: String(item.key),
                      value: item.value,
                    }))}
                    dropdownItemKey="key"
                    dropdownItemDisplay="value"
                    sizeVariant="large"
                    onSelectionChange={(item) =>
                      setSelectedClassification(
                        item as unknown as IclassificationList
                      )
                    }
                    clearSelection
                    dataTestId="asd-tags-and-folders"
                  />
                </Box>
                <Box className="propagate-folder-container">
                  <Typography
                    className="propagate-folder-text"
                    data-testid={`tags-folders-tab-content-propagate-tag-to`}
                  >
                    {t('tagsAndFolders.modal.propagateTagTo')}
                  </Typography>
                  <ArrTooltipWrapper
                    title={t('tagsAndFolders.modal.propagateTooltipTitle')}
                    trigger={
                      <IconButton
                        disableRipple
                        className="info-icon-button"
                        aria-label={t('tagsAndFolders.modal.moreInfo')}
                      >
                        <InfoIcon
                          fontSize="medium"
                          className="info-icon"
                          aria-hidden="true"
                          focusable="false"
                        />
                      </IconButton>
                    }
                  >
                    <p
                      data-testid={`tags-folders-tab-content-propagate-tooltip-desc`}
                    >
                      {t('tagsAndFolders.modal.propagateTooltipDesc')}
                    </p>
                  </ArrTooltipWrapper>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={familyMembers}
                    onChange={(value) => setFamilyMembers(value)}
                    aria-label="select-family-members"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-family-members`}
                  >
                    {t('tagsAndFolders.modal.familyMembers')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={exactDuplicates}
                    onChange={(value) => setExactDuplicates(value)}
                    aria-label="exact-duplicates"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-exact-duplicates`}
                  >
                    {t('tagsAndFolders.modal.exactDuplicates')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={emailThreads}
                    onChange={(value) => setEmailThreads(value)}
                    aria-label="select-email-threads"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-email-threads`}
                  >
                    {t('tagsAndFolders.modal.emailThreads')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={emailDuplicates}
                    onChange={(value) => setEmailDuplicates(value)}
                    aria-label="select-email-duplicates"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-email-duplicates`}
                  >
                    {t('tagsAndFolders.modal.emailDuplicates')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </>
      );
    } else if (type === 'folder') {
      const showFolderGroupContent =
        folderOperation === FolderActions.ADD_GROUP;
      const showFolderContent =
        folderOperation === FolderActions.ADD ||
        (folderOperation === FolderActions.EDIT && !selectedItemObj?.isGroup);
      const showFolderGroupEditContent =
        folderOperation === FolderActions.EDIT && selectedItemObj?.isGroup;

      let displayName = '';
      let displayError = '';

      if (showFolderGroupContent || showFolderGroupEditContent) {
        displayName = folderGroupName;
        displayError = folderGroupNameError;
      } else if (showFolderContent) {
        displayName = folderName;
        displayError = folderNameError;
      }

      content = (
        <>
          <Typography
            id="arr-modal-tag-title"
            className="typo-text headline3_size tag-group-title"
            data-testid={`tags-folders-tab-content-folder-title`}
          >
            {folderTitle}
          </Typography>
          <Box className="text-field-area">
            <ArrTextField
              id="tbt-folder-name-input"
              name="folderName"
              className="option-number"
              sizeVariant="large"
              value={displayName}
              onChange={(e) => {
                const error = validateName(e.target.value);
                if (showFolderGroupContent || showFolderGroupEditContent) {
                  setFolderGroupNameError(error);
                  setFolderGroupName(e.target.value);
                } else if (showFolderContent) {
                  setFolderNameError(error);
                  setFolderName(e.target.value);
                }
              }}
              showErrorIcon={Boolean(displayError)}
              state={displayError ? 'error' : 'enabled'}
              label={t('tagsAndFolders.modal.nameAstric')}
              dataTestId="tbt-folder-name-input"
              supportText={displayError}
              slotProps={{
                htmlInput: {
                  maxLength: 255,
                  'aria-label': t('tagsAndFolders.modal.nameAstric'),
                },
              }}
            />
            {/* Show folder group label when creating/editing folder group */}
            {(showFolderGroupContent || showFolderGroupEditContent) && (
              <Typography
                id="arr-modal-tag-description"
                className="typo-text body4_size text-field-label"
                data-testid={`tags-folders-tab-content-folder-group-modal-label`}
              >
                {t('tagsAndFolders.modal.folderGroupModalLabel')}
              </Typography>
            )}
            {/* Show folder controls only when creating/editing individual folder */}
            {showFolderContent && (
              <Box>
                <Box className="propagate-folder-container">
                  <Typography
                    className="propagate-folder-text"
                    data-testid={`tags-folders-tab-content-propagate-folder-to`}
                  >
                    {t('tagsAndFolders.modal.propagateFolderTo')}
                  </Typography>
                  <ArrTooltipWrapper
                    title={t('tagsAndFolders.modal.propagateFolderTo')}
                    trigger={
                      <IconButton
                        disableRipple
                        className="info-icon-button"
                        aria-label={t('tagsAndFolders.modal.moreInfo')}
                      >
                        <InfoIcon
                          fontSize="medium"
                          className="info-icon"
                          aria-hidden="true"
                          focusable="false"
                        />
                      </IconButton>
                    }
                  >
                    <p
                      data-testid={`tags-folders-tab-content-propagate-tooltip-desc-text`}
                    >
                      {t('tagsAndFolders.modal.propagateTooltipDesc')}
                    </p>
                  </ArrTooltipWrapper>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={familyMembers}
                    onChange={(value) => setFamilyMembers(value)}
                    aria-label="select-family-members-folder-content"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-family-members-text`}
                  >
                    {t('tagsAndFolders.modal.familyMembers')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={exactDuplicates}
                    onChange={(value) => setExactDuplicates(value)}
                    aria-label="select-exact-duplicates-folder-content"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-exact-duplicates-text`}
                  >
                    {t('tagsAndFolders.modal.exactDuplicates')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={emailThreads}
                    onChange={(value) => setEmailThreads(value)}
                    aria-label="select-threads-folder-content"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-email-threads-text`}
                  >
                    {t('tagsAndFolders.modal.emailThreads')}
                  </Typography>
                </Box>
                <Box className="checkbox-container">
                  <ArrCheckbox
                    checked={emailDuplicates}
                    onChange={(value) => setEmailDuplicates(value)}
                    aria-label="select-duplicates-folder-content"
                  />
                  <Typography
                    className="checkbox-text"
                    data-testid={`tags-folders-tab-content-email-duplicates-text`}
                  >
                    {t('tagsAndFolders.modal.emailDuplicates')}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </>
      );
    }
    return content;
  };

  const tagTitle = useMemo(() => {
    if (tagOperation === 'add') {
      return t('tagsAndFolders.add.title');
    } else if (tagOperation === 'edit') {
      return t('tagsAndFolders.edit.title');
    } else if (tagOperation === 'add_tag') {
      return t('tagsAndFolders.modal.newTag');
    } else if (tagOperation === 'edit_tag') {
      return t('tagsAndFolders.modal.editTag');
    }
  }, [tagOperation]);

  const folderTitle = useMemo(() => {
    if (folderOperation === FolderActions.ADD_GROUP) {
      return t('tagsAndFolders.modal.addFolderGroup');
    } else if (folderOperation === FolderActions.ADD) {
      return t('tagsAndFolders.modal.newFolder');
    } else if (folderOperation === FolderActions.EDIT) {
      return selectedItemObj?.isGroup
        ? t('tagsAndFolders.modal.editFolderGroup')
        : t('tagsAndFolders.modal.editFolder');
    }
  }, [folderOperation, selectedItemObj?.isGroup]);

  const callNavigationMasterListAPI = (count: number) => {
    const idsToSend =
      selectedItemIds.length > 0
        ? selectedItemIds
        : selectedItem
          ? [selectedItem]
          : [];
    const payload = {
      sourceInfo: {
        sourceDetails: idsToSend.map((id) => ({
          sourceID: String(id),
        })),
        sourceTypeCode: type === 'tags' ? 'TAG' : 'FOLDER',
        destinationContextTypeCode: docLimitSourceTypeCode,
        totalDocumentToView: count,
      },
    };
    setIsLoading(true);
    setIsNavigating(true);
    getNavigationMasterListId(payload as any, {
      onSuccess: (response) => {
        const masterId = response.reportMasterDoclistId ?? 0;
        setDocListMasterId(masterId);
      },
      onError: (error) => {
        triggerToast(error.message, ToastType.Error);
        setIsLoading(false);
      },
    });
  };

  const navigateToDocPage = () => {
    dispatch(storeSourceTypeCode(docLimitSourceTypeCode));

    if (selectedAction == 'view_in_docView') {
      dispatch(storeDocumentListMasterId(String(docListMasterId)));

      navigate('/DocumentViewer/DocView');
    } else if (selectedAction == 'view_in_docView_in_new_tab') {
      dispatch(storeDocumentListMasterId(String(docListMasterId)));

      const newWindow = window.open('about:blank', '_blank');
      newWindow &&
        (newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`);
    } else if (selectedAction == 'view_in_docList') {
      dispatch(setDocListId(docListMasterId));
      const backPath = `${window.location.pathname}${window.location.search}`;
      dispatch(
        setDocListBackNavigation({
          label: t('tagsAndFolders.title'),
          path: backPath,
        })
      );
      navigate('/Document/DocList');
    }
  };

  useEffect(() => {
    if (!isNavigating) return;
    const {navigationDestinationType: navType, notificationId} =
      lastEvent?.payload?.payload || {};

    if (lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_COMPLETE) {
      if (
        notificationId === Number(docListMasterId) &&
        (navType === SourceTypeCode.DOCVIEW ||
          navType === SourceTypeCode.DOCLIST)
      ) {
        setIsLoading(false);
        setIsNavigating(false);
        navigateToDocPage();
      }
      return;
    }

    if (
      lastEvent?.payload?.type === BgNavigationTypes.NAV_PAYLOAD_ERROR &&
      Number(notificationId) === 0
    ) {
      setIsLoading(false);
      triggerToast(
        t('docExplorer.docDataGrid.navigationErrorToastText'),
        ToastType.Error
      );
      return;
    }
  }, [lastEvent, docListMasterId]);

  const onContinueDocLimit = (count: number) => {
    setShowDocLimitWarning(false);
    switch (docLimitSourceTypeCode) {
      case SourceTypeCode.DOCVIEW: {
        callNavigationMasterListAPI(count);
        break;
      }
      case SourceTypeCode.DOCLIST: {
        callNavigationMasterListAPI(count);
        break;
      }
      default: {
        break;
      }
    }
  };

  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitWarning(false);
    setDocCount(0);
  }, []);

  return (
    <Box className="tags-folders-tab-content">
      <Box className="action-container">
        <Box className="filters-section">
          <Box className="toggle-group">
            <ArrToggle
              tabIndex={0}
              value={isToggleActive}
              size="medium"
              onChange={(e) => handleActionToggle(e.target.checked)}
              dataTestId="action-tbt-toggle"
              ariaLabel={t('assignment.toggleButtonTitle')}
            />
            <Typography
              className="typo-text body3_size"
              data-testid={`tags-folders-tab-content-toggle-button-title`}
            >
              {t('assignment.toggleButtonTitle')}
            </Typography>
          </Box>
          <Box
            className={
              loginUserData.roleId !== 1
                ? 'security-group-dropdown-disabled'
                : ''
            }
          >
            <ArrSingleSelectDropdown
              tabIndex={0}
              selectedValues={selectedSecurityGroup}
              dropdownLabel=""
              dropdownItems={securityGroupList as ISelectItem[]}
              dropdownItemKey="securityGroupID"
              dropdownItemDisplay="securityGroupName"
              customWidth={17}
              sizeVariant="medium"
              onSelectionChange={setSelectedSecurityGroup}
              disabled={loginUserData.roleId !== 1}
              searchable
              showNoResultsText
              dataTestId="arr-dropdown-all-security-groups"
            />
          </Box>
          <Box>
            <ArrTextField
              id="tags-folders-keyword-filter"
              name="tagsFoldersKeywordFilter"
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`tagsAndFolders.filterPlaceholder`)}
              aria-label={t(`tagsAndFolders.filterPlaceholder`)}
              dataTestId="tags-folders-keyword-filter"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (e.target.value.trim() === '') {
                  searchKeyword !== '' && setSelectedItem('');
                  setSearchKeyword('');
                  setSelectedItemsCount(0);
                }
              }}
              onKeyDown={handleSearchKeyEnter}
              type="search"
              onClear={() => {
                setSearchInput('');
                setSearchKeyword('');
                setSelectedItem('');
                setSelectedItemsCount(0);
              }}
            />
          </Box>
        </Box>
        <Box>
          <ActionMenuWrapper
            dataTestId="tags-and-folder-menu"
            actions={actionMenuItems}
            onActionPick={handleActionMenuItemClick}
            size="small"
            sourceType={
              type === 'tags' ? SourceTypeCode.TAG : SourceTypeCode.FOLDER
            }
            sourceTypeContext={
              type === 'tags'
                ? SourceTypeContextCode.MANAGETAGNOC
                : SourceTypeContextCode.MANAGEFOLDERNOC
            }
            getSourceDetails={(sourceDetails, _event, _action) => {
              if (selectedItemIds.length > 0) {
                const LstBulkActionContext = selectedItemIds.map((id) => ({
                  ID: Number(id),
                }));
                return {...sourceDetails, LstBulkActionContext};
              }
              return sourceDetails;
            }}
          />
        </Box>
      </Box>
      {isLoadingTagsList || isLoadingViewData || isLoadingFoldersList ? (
        <Box
          className="loading-container"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <ArrLoadingSpinner />
        </Box>
      ) : (
        <Box
          className="tree-container"
          role="region"
          aria-label={
            type === 'tags'
              ? t('tagsAndFolders.tags')
              : t('tagsAndFolders.folders')
          }
        >
          <ArrTreeView
            setSelectedItem={handleSelected}
            itemsList={getTreeDataList}
            selectedItem={selectedItem}
            handleNoResultMessage={t('common.noResultFound')}
            allowGlobalSelect={false}
            multipleSelection={false}
            enableDragAndDrop={true}
            isMultiItemsDragable={true}
            afterDragEnd={(source, target) => {
              const parentIds = getAllParentIds(
                getTreeDataList,
                String(target?.id)
              );

              const nextExpandedIds = _.uniq([
                '-1',
                ...parentIds,
                String(target?.id),
              ]);
              setExpandedIds(nextExpandedIds);
              if (type === 'folder') {
                handleMoveFolderOrFolderGroup(source, target);
              } else if (type === 'tags') {
                handleMoveTagOrTagGroup(source, target);
              }
            }}
            isShowCheckbox={false}
            showActions={false}
            isShowCountLabel={true}
            showTooltip
            searchKeyword={searchKeyword}
            defaultExpandedIds={initialExpandedIdsRef?.current}
            expandOnSearch={true}
            checkDepth={setCurrentDepth}
            expandSelect={false}
          />
        </Box>
      )}
      <ArrModal {...modalProps(type)}>
        <Box className="modal-container">{modalContent(type)}</Box>
      </ArrModal>
      <ArrModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        className="tag-group-modal"
        dataTestId="arr-modal-edit-tag"
        buttons={[
          {
            id: '1',
            label: t('common.no'),
            onClick: () => setShowEditModal(false),
            variant: 'secondary',
            dataTestId: 'tag-modal-cancel-btn-edit-tag',
          },
          {
            id: '1',
            label: t('common.yes'),
            onClick: addEditTagGroupApiCall,
            variant: 'primary',
            dataTestId: 'tag-modal-save-btn-edit-tag',
          },
        ]}
        title={t('tagsAndFolders.modal.editTag')}
        description={t('tagsAndFolders.modal.editTagDescription')}
      >
        <Box></Box>
      </ArrModal>
      <ArrModal
        showModal={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        className="tag-group-modal"
        dataTestId="arr-modal-delete-tag"
        buttons={[
          {
            id: '1',
            label: t('common.no'),
            onClick: () => setShowDeleteModal(false),
            variant: 'secondary',
            dataTestId: 'tag-modal-no-btn-delete-tag',
          },
          {
            id: '1',
            label: t('common.yes'),
            onClick: handleDeleteModal,
            variant: 'primary',
            dataTestId: 'tag-modal-yes-btn-delete-tag',
          },
        ]}
        title={t('tagsAndFolders.modal.delete.title')}
        description={deleteModalDescription}
        showClose={false}
      >
        <Box></Box>
      </ArrModal>
      {showToast && (
        <ArrToastNotification
          data-testid="tag-toast"
          message={toastMessage}
          onClose={() => {
            setToastMessage('');
            setShowToast(false);
          }}
          status={[toastStatus]}
        />
      )}
      <ArrModal
        showModal={showFolderEditModal}
        onClose={() => setShowFolderEditModal(false)}
        className="tag-group-modal"
        dataTestId="arr-modal-edit-folder"
        buttons={[
          {
            id: '1',
            label: t('common.no'),
            onClick: () => setShowFolderEditModal(false),
            variant: 'secondary',
            dataTestId: 'folder-modal-cancel-btn-edit',
          },
          {
            id: '2',
            label: t('common.yes'),
            onClick: () => {
              setShowFolderEditModal(false);
              updateFolderApiCall();
            },
            variant: 'primary',
            dataTestId: 'folder-modal-save-btn-edit',
          },
        ]}
        title={t('tagsAndFolders.modal.editFolder')}
        description={t('tagsAndFolders.modal.editFolderDescription')}
        showClose={false}
      >
        <Box></Box>
      </ArrModal>
      <ArrDocMaxLimitWarning
        totalDocumentCount={docCount}
        show={showDocLimitWarning}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
      {isLoading && <ArrLoadingSpinner overlay isOpen={isLoading} />}
    </Box>
  );
};

export default TagsFoldersTabContent;
